/**
 * _placeholder — 「未建立遊戲」的通用入口（dynamic entry）。
 *
 * 解決的痛點：Cocos 要看自己的遊戲 iframe，得先等 host 這邊「建好一款遊戲」
 * （content.ts + assets.ts + registry 註冊 + 白名單 + 推 dev/stg）才有入口。但他們
 * 真正需要的只是一個**路口**：把 iframe 指到 `${GAME_H5_URL}/games/<gameCode>/index.html`
 * 就能看了，paytable / rules / history 空著沒關係。
 *
 * 設計：placeholder 是 **API fallback**，不是 API 之前的閘。fetchGameCode 的解析順序：
 *
 *   1. native 遊戲（NATIVE_HELP_GAMES）       → 本地 content（不變）
 *   2. shared-web 遊戲（後台 fetchGameInfo 有）→ 用後台回的真 gameUrl（行為完全不變）
 *   3. 連後台都查無的 gameId（真正沒建）       → 才合成 placeholder 空殼
 *
 * 這樣「以前走 shared-web 的遊戲」一律先打 API 拿到真設定，**完全不受影響**；只有
 * 連後台都不知道的 gameId 才落到 placeholder → 免旗標、也不會誤傷。
 *
 * 安全閘：只在 local / dev / stg 開放（Cocos 預覽未建好的遊戲）。sandbox / rel / prod
 * 一律關閉 → API 查無就維持原本「未知 gameId → 報錯」行為。對齊 vite.config.ts 的
 * ENABLED_DEV_LOGIN_MODES 慣例。
 *
 * 標記機制：fetchGameCode 落到 placeholder 時呼叫 markPlaceholderResolved(gameId)；
 * 之後同步的 help 解析（isNativeHelpGame / getContent / resolveNativeHelpComponent）
 * 靠 isPlaceholderResolved(gameId) 知道「這 gameId 是 placeholder」→ help modal 走
 * host 原生空殼，而非破掉的 shared-web iframe。
 *
 * 之後要「未建立但顯示真實 paytable/rules」→ 把 buildPlaceholderContent 換成「先 fetch
 * 一份外部 JSON、有就用、沒有退空殼」即可，seam 同一處、不動其他檔。
 */

import { GAME_H5_URL } from '../gameConfig'
import { APP_ENV } from '../runtimeConfig'
import type { GameContent, GameLocalConfig } from './types'

/**
 * 只在 local / dev / stg 開放 placeholder。sandbox / rel / prod 一律關閉。
 * 對齊 runtimeConfig.ts 的 ENABLED_DEV_LOGIN_MODES 慣例（吃 config.json.env）。
 */
const PLACEHOLDER_MODES = ['local', 'dev', 'stg'] as const
export function isPlaceholderModeAllowed(): boolean {
  return (PLACEHOLDER_MODES as readonly string[]).includes(APP_ENV)
}

// ── 已解析為 placeholder 的 gameId（fetchGameCode 在 API 查無後標記）────────────
const resolved = new Set<string>()

/** fetchGameCode 落到 placeholder 時呼叫，讓後續同步 help 解析認得這個 gameId */
export function markPlaceholderResolved(gameId: string): void {
  resolved.add(gameId.toLowerCase())
}

/** 這個 gameId 是否已被解析為 placeholder（help modal / content 解析用） */
export function isPlaceholderResolved(gameId: string | null | undefined): boolean {
  if (!gameId) return false
  return resolved.has(gameId.toLowerCase())
}

// ── gameUrl / config / content 合成 ─────────────────────────────────────────

/** 讀當前 URL 的 query（SSR-safe；無 window 時回 null） */
function readGameCodeFromUrl(): string | null {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('gameCode') || null
}

/** gameCode 解析：URL 指定 > gameId 本身 */
function resolveGameCode(gameId: string): string {
  return readGameCodeFromUrl() || gameId
}

/** 用標準 pattern 從 gameCode 湊 Cocos iframe URL（對齊各 content.ts 的 `${GAME_H5_URL}/games/<path>/index.html`） */
export function buildPlaceholderGameUrl(gameId: string): string {
  return `${GAME_H5_URL}/games/${resolveGameCode(gameId)}/index.html`
}

/**
 * 合成最小 GameLocalConfig（fetchGameCode 在 API 查無後用）。
 * gameName 直接用 gameId（沒有真名時的佔位）；主題給中性白。
 */
export function buildPlaceholderConfig(gameId: string): GameLocalConfig {
  const code = resolveGameCode(gameId)
  return {
    key: gameId,
    gameCode: code,
    gameUrl: buildPlaceholderGameUrl(gameId),
    gameName: { zh: gameId, en: gameId },
    assets: {
      themeColor: '#ffffff',
      transitionTextColor: '#ffffff',
    },
  }
}

/**
 * 合成最小 GameContent（registry.getContent fallback）。
 * paytable / rules 空 sections → 共用 modal 渲染空白；history 給最小盤面參數，
 * 共用 HistoryDetailModal 顯示空盤（無自訂盤面元件、無 extractor）。
 */
export function buildPlaceholderContent(gameId: string): GameContent {
  const code = resolveGameCode(gameId)
  return {
    identity: {
      key: gameId,
      gameCode: code,
      gameUrl: buildPlaceholderGameUrl(gameId),
      gameName: { zh: gameId, en: gameId },
    },
    chrome: {
      themeColor: '#ffffff',
      transitionTextColor: '#ffffff',
    },
    rules: { sections: [] },
    paytable: { sections: [], symbols: [] },
    history: {
      boardShape: '33333',
      rowsPerCol: 3,
      symbolMap: {},
    },
  }
}
